import * as React from 'react';

interface OidcAccountDetailsFormProps {
  /** Email captured from the verified IdP identity, displayed read-only as
   * confirmation that the user is signing up under the right account. */
  email?: string;
  onSubmit: (values: { company: string; role: string }) => Promise<void>;
}

export const OidcAccountDetailsForm: React.FC<OidcAccountDetailsFormProps> = ({
  email,
  onSubmit,
}) => {
  const [company, setCompany] = React.useState('');
  const [role, setRole] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const trimmedCompany = company.trim();
  const trimmedRole = role.trim();
  const canSubmit = Boolean(trimmedCompany && trimmedRole) && !submitting;

  const handleSubmit = React.useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!trimmedCompany || !trimmedRole) return;

      setSubmitting(true);
      setError(null);

      try {
        await onSubmit({ company: trimmedCompany, role: trimmedRole });
      } catch (_err) {
        setError('Something went wrong. Please try again.');
        setSubmitting(false);
      }
    },
    [trimmedCompany, trimmedRole, onSubmit],
  );

  return (
    <div className="oidc-account-details-card">
      <form className="oidc-account-details-form" onSubmit={handleSubmit} noValidate>
        <div className="oidc-form-header">
          <h2 className="oidc-form-title">Tell us a bit more about you</h2>
          <p className="oidc-form-subtitle">
            {email
              ? `Signing up as ${email}. We just need a couple more details.`
              : 'We just need a couple more details to finish creating your account.'}
          </p>
        </div>

        <div className="oidc-form-row">
          <label htmlFor="oidc-company">Company name</label>
          <input
            id="oidc-company"
            type="text"
            value={company}
            onChange={e => setCompany(e.target.value)}
            required
            disabled={submitting}
            placeholder="Your company name"
            autoComplete="organization"
          />
        </div>

        <div className="oidc-form-row">
          <label htmlFor="oidc-role">Job title</label>
          <input
            id="oidc-role"
            type="text"
            value={role}
            onChange={e => setRole(e.target.value)}
            required
            disabled={submitting}
            placeholder="e.g. Data Engineer"
            autoComplete="organization-title"
          />
        </div>

        {error && (
          <p className="oidc-form-error" role="alert">
            {error}
          </p>
        )}

        <button className="oidc-submit-button" type="submit" disabled={!canSubmit}>
          {submitting ? 'Creating account...' : 'Create account'}
        </button>
      </form>
    </div>
  );
};
